import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { FilterTypeModel } from '../../../Common/Filter/models/abstract-filter-type.model';
import { SortOrderServiceInterface } from '../../../Common/Misc/services/sort-order.service.interface';
import { NewsroomSideBarItemModel } from '../../Shared/models/newsroom-side-bar-item.model';
import { LocalizedEnum } from '../../../api/journodex/model/localized-enum';
import { SortOrderEnum } from '../../../api/shared/enums/sort-order.enum';
import { AuditableFilterService } from '../../../Common/Filter/services/auditable-filter.service';
import { UsersService } from '../../../Core/Users/services/users.service';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { StatusEnum } from '../enums/status.enum';
import { FilterTypeCheckboxModel } from '../../../Common/Filter/models/filter-type-checkbox.model';
import { DateQueryModel } from '../../../Common/Forms/models/date-query.model';
import { FilterOptionModel } from '../../../Common/Filter/models/filter-option.model';
import { StoriesConstants } from '../components/stories/stories-constants';


@Injectable()
export class NewsroomLeftSidebarService extends AuditableFilterService implements SortOrderServiceInterface {
    stories: NewsroomSideBarItemModel[] = [];
    protected listItems: NewsroomSideBarItemModel[];
    protected listItems$: BehaviorSubject<NewsroomSideBarItemModel[]>;

    protected sortOrder: SortOrderEnum = SortOrderEnum.ascending;
    protected sortOrder$: BehaviorSubject<SortOrderEnum> = new BehaviorSubject<SortOrderEnum>(this.sortOrder);
    protected sortField: LocalizedEnum = null;
    protected sortField$: BehaviorSubject<LocalizedEnum> = new BehaviorSubject<LocalizedEnum>(this.sortField);
    protected allFilters: FilterTypeModel[];

    protected lhpSortFields$: BehaviorSubject<LocalizedEnum[]> = new BehaviorSubject<LocalizedEnum[]>([]);
    protected lhpSortFields: LocalizedEnum[];

    protected selectedSortField: LocalizedEnum = null;
    protected selectedSortField$: BehaviorSubject<LocalizedEnum> = new BehaviorSubject<LocalizedEnum>(this.selectedSortField);

    protected readonly sortFieldCreatedDate: LocalizedEnum = {
        enumValue: 'CREATED_DATE',
        displayString: 'Reports.sharedReports.sortFields.createdDate'
    };
    protected readonly sortFieldName: LocalizedEnum = {
        enumValue: 'NAME',
        displayString: 'Reports.sharedReports.sortFields.name'
    };
    protected readonly sortFieldStatus: LocalizedEnum = {
        enumValue: 'STATUS',
        displayString: 'Discover.sortFields.status'
    };
    protected readonly storyStatusFilter = new FilterTypeCheckboxModel<string>(
        'status',
        'Amplify.leftSidebar.wire.filter.status',
        [
            {
                label: 'Amplify.filter.status.draft',
                value: StoriesConstants.statusLabels[StatusEnum.DRAFT]
            },
            {
                label: 'Amplify.filter.status.inReview',
                value: StoriesConstants.statusLabels[StatusEnum.INREVIEW]
            },
            {
                label: 'Amplify.filter.status.scheduled',
                value: StoriesConstants.statusLabels[StatusEnum.SCHEDULED]
            },
            {
                label: 'Amplify.filter.status.published',
                value: StoriesConstants.statusLabels[StatusEnum.PUBLISHED]
            },
            {
                label: 'Amplify.filter.status.retracted',
                value: StoriesConstants.statusLabels[StatusEnum.RETRACTED]
            },
            {
                label: 'Amplify.filter.status.failed',
                value: StoriesConstants.statusLabels[StatusEnum.FAILED]
            },
            {
                label: 'Amplify.filter.status.failedReview',
                value: StoriesConstants.statusLabels[StatusEnum.FAILED_REVIEW]
            },
            {
                label: 'Amplify.filter.status.moderated',
                value: StoriesConstants.statusLabels[StatusEnum.MODERATED]
            },
        ]
    );



    constructor(
        public usersService: UsersService,
        private coreDialogService: CoreDialogService,
    ) {
        super(usersService);

        this.selectedSortField = this.sortFieldName;
        this.selectedSortField$.next(this.selectedSortField);

        this.lhpSortFields = [this.sortFieldName, this.sortFieldCreatedDate, this.sortFieldStatus];
        this.lhpSortFields$.next(this.lhpSortFields.slice());

        this.listItems = [];
        this.listItems$ = new BehaviorSubject(this.listItems);

        this.nameFilter.label = 'Newsroom.nameFilter.label';

        this.allFilters = [
            this.nameFilter,
            this.createdByFilter,
            this.createdDateFilter,
            this.modifiedByFilter,
            this.modifiedDateFilter,
            this.storyStatusFilter
        ];

        this.setFilters(this.allFilters);
    }

    setListItems(listItems: NewsroomSideBarItemModel[]) {
        this.listItems = listItems;
        this.listItems$.next(this.listItems);
    }

    getListItems(): Observable<NewsroomSideBarItemModel[]> {
        return this.listItems$;
    }

    getFilterOptions(lhpItems: NewsroomSideBarItemModel[]): void {
        this.stories = lhpItems;
        this.getAuditableFilterOptions(lhpItems);
        this.getStatusTypeFilterCounts(lhpItems);
    }

    getSortOrder(): Observable<SortOrderEnum> {
        return this.sortOrder$;
    }

    setSortOrder(order: SortOrderEnum): void {
        if (this.sortOrder !== order) {
            this.sortOrder = order;
            this.sortOrder$.next(order);
            this.applyFilters();
            this.listItems$.next(this.listItems);
        }
    }


    getSortableFields(): Observable<any[]> {
        return this.lhpSortFields$;
    }

    getSortField(): Observable<any> {
        return this.selectedSortField$;
    }
    setSortField(field: any): void {
        if (field !== this.selectedSortField) {
            this.selectedSortField = field;
            this.selectedSortField$.next(field);
            this.applyFilters();
            this.listItems$.next(this.listItems);
        }
    }

    private sortList() {
        let returnValue1, returnValue2;
        if (this.sortOrder === SortOrderEnum.ascending){
            returnValue1 = 1;
            returnValue2 = -1;
        }else{
            returnValue1 = -1;
            returnValue2 = 1;
        }

        this.listItems.sort((a, b) => {
            if (this.selectedSortField === this.sortFieldName){
                // name
                return a.name.toLocaleLowerCase() > b.name.toLocaleLowerCase() ?  returnValue1 : returnValue2;
            }else if (this.selectedSortField === this.sortFieldCreatedDate){
                // create date
                return a.createdDate > b.createdDate ? returnValue1 : returnValue2;
            }else if (this.selectedSortField === this.sortFieldStatus){
                // status
                return a.status > b.status ? returnValue1 : returnValue2;
            }
        });

        this.listItems = this.listItems.slice();
    }


    private getStatusTypeFilterCounts(items: NewsroomSideBarItemModel[]) {
        const optionsMap: Map<string, FilterOptionModel<string>> = new Map();
        this.storyStatusFilter.options.forEach(opt => {
            opt.count = 0;
            optionsMap.set(opt.value, opt);
        });
        items.forEach(item => {
            if (item.status) {
                optionsMap.get(item.status).count++;
            }
        });
    }

    private applyStatusFilters(items: NewsroomSideBarItemModel[]): NewsroomSideBarItemModel[] {
        const filterData = this.storyStatusFilter.getValue();

        if (!filterData || filterData.length < 1) {
            return items;
        }

        return items.filter(item =>  filterData.includes(item.status));
    }

    applyFilters() {
        super.applyFilters();

        this.listItems = this.stories;
        this.listItems = this.applyNameFilters(this.listItems);
        this.listItems = this.applyAuditableFilters(this.listItems);
        this.listItems = this.applyStatusFilters(this.listItems);

        this.sortList();
        this.listItems$.next(this.listItems);
    }



    /**
     * Resets all of the filters
     */
    private resetFilterOptions(): void {
        this.nameFilter.setValue([]);
        this.createdByFilter.setValue([]);
        this.createdByFilter.options = [];
        this.modifiedByFilter.setValue([]);
        this.modifiedByFilter.options = [];
        this.modifiedDateFilter.setValue(DateQueryModel.getAllQuery());
        this.storyStatusFilter.setValue([]);
    }
}
